<template>
  <HrPageLayout title="Compliance Unit" subtitle="Monitor and manage employee compliance obligations" icon="🛡️">

    <div class="cu-page">

      <!-- ── Toolbar ── -->
      <div class="cu-toolbar">
        <div class="cu-tabs">
          <button class="cu-tab" :class="{ 'cu-tab-active': activeTab === 'skills_missing' }" @click="switchTab('skills_missing')">
            <i class="bx bx-book-open"></i>
            <span>Skills Not Submitted</span>
            <span v-if="activeTab === 'skills_missing' && summary.non_compliant" class="cu-tab-badge cu-badge-danger">{{ summary.non_compliant }}</span>
          </button>
          <button class="cu-tab" :class="{ 'cu-tab-active': activeTab === 'wealth_missing' }" @click="switchTab('wealth_missing')">
            <i class="bx bx-wallet"></i>
            <span>Wealth Declaration</span>
            <span v-if="activeTab === 'wealth_missing' && summary.non_compliant" class="cu-tab-badge cu-badge-danger">{{ summary.non_compliant }}</span>
          </button>
          <button class="cu-tab" :class="{ 'cu-tab-active': activeTab === 'confirmation_due' }" @click="switchTab('confirmation_due')">
            <i class="bx bx-user-check"></i>
            <span>Confirmation Due</span>
            <span v-if="activeTab === 'confirmation_due' && rows.length" class="cu-tab-badge cu-badge-warning">{{ rows.length }}</span>
          </button>
        </div>
        <div class="cu-toolbar-right">
          <div class="cu-year-select">
            <i class="bx bx-calendar"></i>
            <select v-model.number="year" @change="loadData">
              <option v-for="y in yearOptions" :key="'cy-'+y" :value="y">{{ y }}</option>
            </select>
          </div>
          <button class="cu-btn cu-btn-ghost" @click="loadData" title="Refresh">
            <i class="bx bx-refresh"></i><span>Refresh</span>
          </button>
          <button class="cu-btn cu-btn-outline" @click="exportReport">
            <i class="bx bx-download"></i><span>Export</span>
          </button>
          <button class="cu-btn cu-btn-primary" :disabled="sendingAll" @click="sendEmailToAll">
            <i class="bx" :class="sendingAll ? 'bx-loader-alt bx-spin' : 'bx-send'"></i>
            <span>{{ sendingAll ? 'Sending...' : 'Email All' }}</span>
          </button>
        </div>
      </div>

      <!-- ── Summary Cards ── -->
      <div class="cu-summary-row">
        <div class="cu-summary-card cu-card-total">
          <div class="cu-summary-icon"><i class="bx bx-group"></i></div>
          <div class="cu-summary-info">
            <span class="cu-summary-label">Total Employees</span>
            <span class="cu-summary-value">{{ summary.total }}</span>
          </div>
        </div>
        <div class="cu-summary-card cu-card-compliant">
          <div class="cu-summary-icon"><i class="bx bx-check-circle"></i></div>
          <div class="cu-summary-info">
            <span class="cu-summary-label">Compliant</span>
            <span class="cu-summary-value">{{ summary.compliant }}</span>
          </div>
          <div class="cu-summary-pct" v-if="summary.total > 0">
            {{ Math.round((summary.compliant / summary.total) * 100) }}%
          </div>
        </div>
        <div class="cu-summary-card cu-card-noncompliant">
          <div class="cu-summary-icon"><i class="bx bx-x-circle"></i></div>
          <div class="cu-summary-info">
            <span class="cu-summary-label">Non-Compliant</span>
            <span class="cu-summary-value">{{ summary.non_compliant }}</span>
          </div>
          <div class="cu-summary-pct" v-if="summary.total > 0">
            {{ Math.round((summary.non_compliant / summary.total) * 100) }}%
          </div>
        </div>
        <!-- Donut chart -->
        <div class="cu-summary-card cu-card-chart">
          <div class="cu-donut-wrap">
            <div class="cu-donut" :style="pieStyle">
              <div class="cu-donut-hole">
                <span class="cu-donut-pct" v-if="summary.total > 0">{{ Math.round((summary.compliant / summary.total) * 100) }}%</span>
                <span class="cu-donut-label">compliant</span>
              </div>
            </div>
            <div class="cu-donut-legend">
              <div class="cu-legend-item"><span class="cu-legend-dot cu-dot-ok"></span>Compliant: <strong>{{ summary.compliant }}</strong></div>
              <div class="cu-legend-item"><span class="cu-legend-dot cu-dot-bad"></span>Non-compliant: <strong>{{ summary.non_compliant }}</strong></div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Table Card ── -->
      <div class="cu-table-card">
        <div class="cu-table-header">
          <div class="cu-table-title">
            <i class="bx bx-list-ul"></i>
            <span v-if="activeTab === 'skills_missing'">Employees — Skills Not Submitted ({{ year }})</span>
            <span v-else-if="activeTab === 'wealth_missing'">Employees — Wealth Declaration Not Submitted ({{ year }})</span>
            <span v-else>Employees — Confirmation Due</span>
          </div>
          <span class="cu-row-count">{{ rows.length }} record{{ rows.length !== 1 ? 's' : '' }}</span>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="cu-loading">
          <div class="cu-spinner"></div>
          <span>Loading compliance data...</span>
        </div>

        <!-- Empty -->
        <div v-else-if="!rows.length" class="cu-empty">
          <i class="bx bx-check-shield"></i>
          <h5>All Clear</h5>
          <p>No non-compliant employees found for {{ year }}.</p>
        </div>

        <!-- Table -->
        <div v-else class="cu-table-wrap">
          <table class="cu-table">
            <thead>
              <tr v-if="activeTab !== 'confirmation_due'">
                <th>#</th>
                <th>Employee</th>
                <th>Emp No</th>
                <th>ID No</th>
                <th>Department</th>
                <th>Position</th>
                <th>Action</th>
              </tr>
              <tr v-else>
                <th>#</th>
                <th>Employee</th>
                <th>Emp No</th>
                <th>ID No</th>
                <th>Department</th>
                <th>Probation End</th>
                <th>Days Left</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, idx) in rows" :key="`comp-${activeTab}-${r.employee_id}-${r.empNo}`">
                <td class="cu-td-num">{{ idx + 1 }}</td>
                <td>
                  <div class="cu-employee-cell">
                    <div class="cu-avatar">{{ initials(r.employee_name) }}</div>
                    <span>{{ r.employee_name }}</span>
                  </div>
                </td>
                <td><span class="cu-mono">{{ r.empNo || '—' }}</span></td>
                <td><span class="cu-mono">{{ r.nationalId || '—' }}</span></td>
                <td>{{ r.departmentName || '—' }}</td>
                <td v-if="activeTab !== 'confirmation_due'">{{ r.positionTitle || '—' }}</td>
                <template v-else>
                  <td>{{ r.probation_end || '—' }}</td>
                  <td>
                    <span class="cu-days-badge" :class="Number(r.remaining_days) <= 30 ? 'cu-days-urgent' : 'cu-days-ok'">
                      {{ r.remaining_days }} days
                    </span>
                  </td>
                  <td><span class="cu-status-pill">{{ r.status || 'Ongoing' }}</span></td>
                </template>
                <td>
                  <button
                    class="cu-send-btn"
                    :class="{ 'cu-send-loading': sendingId === r.employee_id }"
                    :disabled="sendingId === r.employee_id"
                    @click="sendEmailOne(r)"
                    title="Send reminder email"
                  >
                    <i class="bx" :class="sendingId === r.employee_id ? 'bx-loader-alt bx-spin' : 'bx-envelope'"></i>
                    <span>{{ sendingId === r.employee_id ? 'Sending...' : 'Send Email' }}</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl, toast } from "@/api";

export default {
  name: "ModuleComplianceUnit",
  components: { HrPageLayout },
  data() {
    const y = new Date().getFullYear();
    return {
      activeTab: "skills_missing",
      year: y,
      yearOptions: [y, y - 1, y - 2],
      loading: false,
      rows: [],
      summary: { compliant: 0, non_compliant: 0, total: 0 },
      debugCounts: null,
      sendingId: null,
      sendingAll: false
    };
  },
  computed: {
    pieStyle() {
      const total = Number(this.summary.total || 0);
      const compliant = Number(this.summary.compliant || 0);
      const pct = total > 0 ? Math.max(0, Math.min(100, (compliant / total) * 100)) : 0;
      return {
        background: `conic-gradient(#198754 0% ${pct}%, #dc3545 ${pct}% 100%)`
      };
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab;
      this.loadData();
    },
    async loadData() {
      this.loading = true;
      try {
        if (this.activeTab === "wealth_missing") {
          await this.loadWealthComplianceFromApproved();
        } else if (this.activeTab === "skills_missing") {
          await this.loadSkillsComplianceFromReport();
        } else {
          const fd = new FormData();
          fd.append("function", "getComplianceOverview");
          fd.append("type", this.activeTab);
          fd.append("year", String(this.year));
          const res = await execute(fd, authUrl);
          if (res?.data?.success) {
            this.rows = res?.data?.data?.rows || [];
            this.summary = res?.data?.data?.summary || { compliant: 0, non_compliant: this.rows.length, total: this.rows.length };
            this.debugCounts = res?.data?.data?.debug_counts || null;
          } else {
            this.rows = [];
            this.summary = { compliant: 0, non_compliant: 0, total: 0 };
            this.debugCounts = null;
            toast.error(res?.data?.message || "Failed to load compliance data");
          }
        }
      } catch (_) {
        this.rows = [];
        this.summary = { compliant: 0, non_compliant: 0, total: 0 };
        this.debugCounts = null;
        toast.error("Failed to load compliance data");
      } finally {
        this.loading = false;
      }
    },
    async fetchAllEmployeesFromRegister() {
      const limit = 500;
      let page = 1;
      const all = [];
      while (page <= 50) {
        const fd = new FormData();
        fd.append("function", "getEmployees");
        fd.append("page", String(page));
        fd.append("limit", String(limit));
        const r = await execute(fd, authUrl);
        const chunk = r?.data?.data?.employees || [];
        if (!chunk.length) break;
        all.push(...chunk);
        if (chunk.length < limit) break;
        page += 1;
      }
      return all;
    },
    mapEmployeeRow(e) {
      return {
        employee_id: e.id,
        employee_name: `${e.firstName || ""} ${e.lastName || ""}`.trim(),
        empNo: e.empNo || e.personalNo || "",
        nationalId: e.nationalId || e.idNo || "",
        departmentName: e.departmentName || "",
        positionTitle: e.positionTitle || e.designation || ""
      };
    },
    async loadWealthComplianceFromApproved() {
      const employees = await this.fetchAllEmployeesFromRegister();
      const fd = new FormData();
      fd.append("function", "getWealthDeclarationComplianceReport");
      fd.append("year", String(this.year));
      const res = await execute(fd, authUrl);
      const data = res?.data?.data?.data || [];
      const approvedEmpNo = new Set();
      const approvedIdNo = new Set();
      data.forEach((r) => {
        const st = (r.status || "").toString().trim().toLowerCase();
        if (st !== "approved") return;
        const emp = (r.emp_no || "").toString().trim().toUpperCase();
        const nid = (r.nationalId || "").toString().trim().toUpperCase();
        if (emp) approvedEmpNo.add(emp);
        if (nid) approvedIdNo.add(nid);
      });
      const non = employees.filter((e) => {
        const emp = (e.empNo || "").toString().trim().toUpperCase();
        const nid = (e.nationalId || "").toString().trim().toUpperCase();
        const ok = (emp && approvedEmpNo.has(emp)) || (nid && approvedIdNo.has(nid));
        return !ok;
      });
      this.rows = non.map(this.mapEmployeeRow);
      this.summary = {
        total: employees.length,
        non_compliant: non.length,
        compliant: Math.max(0, employees.length - non.length)
      };
      this.debugCounts = {
        employees_total: employees.length,
        skills_entries_year: null,
        wealth_approved_year: Math.max(0, employees.length - non.length)
      };
    },
    async loadSkillsComplianceFromReport() {
      const employees = await this.fetchAllEmployeesFromRegister();
      const countsFd = new FormData();
      countsFd.append("function", "getSkillsAuditComplianceCounts");
      countsFd.append("auditYear", String(this.year));
      const countsRes = await execute(countsFd, authUrl);
      const c = countsRes?.data?.data || {};
      const compliant = Number(c.compliantRows || 0);

      const rowsFd = new FormData();
      rowsFd.append("function", "listSkillsAuditReport");
      rowsFd.append("auditYear", String(this.year));
      rowsFd.append("page", "1");
      rowsFd.append("limit", "10000");
      rowsFd.append("compliance", "non-compliant");
      const rowsRes = await execute(rowsFd, authUrl);
      const nonRows = rowsRes?.data?.data?.rows || [];
      this.rows = nonRows.map((r) => ({
        employee_id: r.employee_id || `${r.personal_number || ""}-${r.national_id || ""}`,
        employee_name: r.employee_name || "",
        empNo: r.personal_number || "",
        nationalId: r.national_id || "",
        departmentName: r.department || "",
        positionTitle: r.designation_appointment || r.designation_deployment || ""
      }));
      const nonCompliant = this.rows.length || Math.max(0, employees.length - compliant);
      const total = employees.length || Number(c.totalRows || 0);
      this.summary = {
        total,
        compliant: Math.max(0, Math.min(compliant, total)),
        non_compliant: Math.max(0, nonCompliant)
      };
      this.debugCounts = {
        employees_total: total,
        skills_entries_year: null,
        wealth_approved_year: null
      };
    },
    exportReport() {
      const isConfirmation = this.activeTab === "confirmation_due";
      const headers = isConfirmation
        ? ["Employee", "Emp No", "ID No", "Department", "Probation End", "Remaining Days", "Status"]
        : ["Employee", "Emp No", "ID No", "Department", "Position"];
      const body = this.rows.map((r) => (
        isConfirmation
          ? [r.employee_name || "", r.empNo || "", r.nationalId || "", r.departmentName || "", r.probation_end || "", r.remaining_days ?? "", r.status || ""]
          : [r.employee_name || "", r.empNo || "", r.nationalId || "", r.departmentName || "", r.positionTitle || ""]
      ));
      const csv = [headers, ...body].map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compliance_${this.activeTab}_${this.year}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    async sendEmailOne(row) {
      if (!row?.employee_id) return;
      this.sendingId = row.employee_id;
      try {
        const fd = new FormData();
        fd.append("function", "sendComplianceEmail");
        fd.append("employeeId", String(row.employee_id));
        fd.append("type", this.activeTab);
        fd.append("year", String(this.year));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) toast.success("Email sent.");
        else toast.error(res?.data?.message || "Failed to send email.");
      } catch (_) {
        toast.error("Failed to send email.");
      } finally {
        this.sendingId = null;
      }
    },
    async sendEmailToAll() {
      this.sendingAll = true;
      try {
        const fd = new FormData();
        fd.append("function", "sendComplianceEmailAll");
        fd.append("type", this.activeTab);
        fd.append("year", String(this.year));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          const d = res?.data?.data || {};
          toast.success(`Completed: sent ${d.sent || 0}, failed ${d.failed || 0}.`);
        } else {
          toast.error(res?.data?.message || "Bulk email failed.");
        }
      } catch (_) {
        toast.error("Bulk email failed.");
      } finally {
        this.sendingAll = false;
      }
    },
    initials(name) {
      if (!name) return '?';
      return name.trim().split(/\s+/).slice(0, 2).map(w => w[0].toUpperCase()).join('');
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.cu-page { display: flex; flex-direction: column; gap: 20px; }

/* ── Toolbar ── */
.cu-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  background: #fff; border-radius: 14px; border: 1px solid #e2e8f0;
  padding: 8px 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  flex-wrap: wrap; gap: 10px;
}
.cu-tabs { display: flex; gap: 4px; background: #f1f5f9; border-radius: 10px; padding: 4px; }
.cu-tab {
  display: flex; align-items: center; gap: 7px; padding: 8px 16px;
  border-radius: 8px; border: none; background: transparent;
  color: #64748b; font-size: 13px; font-weight: 600; cursor: pointer;
  transition: all 0.18s; white-space: nowrap;
}
.cu-tab i { font-size: 16px; }
.cu-tab:hover { background: #e2e8f0; color: #1e293b; }
.cu-tab-active { background: #fff !important; color: #059669 !important; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.cu-tab-badge { font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 20px; min-width: 20px; text-align: center; }
.cu-badge-danger { background: #ef4444; color: #fff; }
.cu-badge-warning { background: #f59e0b; color: #fff; }

.cu-toolbar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.cu-year-select {
  display: flex; align-items: center; gap: 6px;
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;
  padding: 6px 10px; color: #475569; font-size: 13px;
}
.cu-year-select i { font-size: 15px; color: #059669; }
.cu-year-select select { border: none; background: transparent; font-size: 13px; font-weight: 600; color: #1e293b; outline: none; cursor: pointer; }

.cu-btn {
  display: flex; align-items: center; gap: 6px; padding: 8px 16px;
  border-radius: 8px; border: 1px solid transparent;
  font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; white-space: nowrap;
}
.cu-btn i { font-size: 15px; }
.cu-btn-ghost { background: #f8fafc; color: #475569; border-color: #e2e8f0; }
.cu-btn-ghost:hover { background: #e2e8f0; color: #1e293b; }
.cu-btn-outline { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
.cu-btn-outline:hover { background: #16a34a; color: #fff; border-color: #16a34a; }
.cu-btn-primary { background: linear-gradient(135deg, #059669, #0d9488); color: #fff; box-shadow: 0 2px 8px rgba(5,150,105,0.3); }
.cu-btn-primary:hover:not(:disabled) { background: linear-gradient(135deg, #047857, #0f766e); transform: translateY(-1px); }
.cu-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Summary Cards ── */
.cu-summary-row { display: grid; grid-template-columns: repeat(3, 1fr) 280px; gap: 16px; }
.cu-summary-card {
  background: #fff; border-radius: 14px; border: 1px solid #e2e8f0;
  padding: 20px; display: flex; align-items: center; gap: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04); position: relative; overflow: hidden;
}
.cu-summary-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; }
.cu-card-total::before { background: #3b82f6; }
.cu-card-compliant::before { background: #059669; }
.cu-card-noncompliant::before { background: #ef4444; }
.cu-card-chart::before { background: linear-gradient(90deg, #059669, #ef4444); }

.cu-summary-icon {
  width: 48px; height: 48px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0;
}
.cu-card-total .cu-summary-icon { background: #eff6ff; color: #3b82f6; }
.cu-card-compliant .cu-summary-icon { background: #f0fdf4; color: #059669; }
.cu-card-noncompliant .cu-summary-icon { background: #fef2f2; color: #ef4444; }

.cu-summary-info { flex: 1; }
.cu-summary-label { display: block; font-size: 11px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.cu-summary-value { display: block; font-size: 28px; font-weight: 800; color: #1e293b; line-height: 1; }
.cu-summary-pct { font-size: 13px; font-weight: 700; padding: 3px 8px; border-radius: 20px; }
.cu-card-compliant .cu-summary-pct { background: #f0fdf4; color: #059669; }
.cu-card-noncompliant .cu-summary-pct { background: #fef2f2; color: #ef4444; }

/* Donut */
.cu-card-chart { flex-direction: column; align-items: flex-start; padding: 16px 20px; }
.cu-donut-wrap { display: flex; align-items: center; gap: 20px; width: 100%; }
.cu-donut {
  width: 90px; height: 90px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.cu-donut-hole {
  width: 60px; height: 60px; border-radius: 50%; background: #fff;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
}
.cu-donut-pct { font-size: 14px; font-weight: 800; color: #1e293b; line-height: 1; }
.cu-donut-label { font-size: 9px; color: #94a3b8; font-weight: 600; text-transform: uppercase; }
.cu-donut-legend { display: flex; flex-direction: column; gap: 8px; }
.cu-legend-item { display: flex; align-items: center; gap: 7px; font-size: 12px; color: #475569; }
.cu-legend-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.cu-dot-ok { background: #059669; }
.cu-dot-bad { background: #ef4444; }

/* ── Table Card ── */
.cu-table-card { background: #fff; border-radius: 14px; border: 1px solid #e2e8f0; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
.cu-table-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid #f1f5f9; background: #f8fafc;
}
.cu-table-title { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 700; color: #1e293b; }
.cu-table-title i { font-size: 18px; color: #059669; }
.cu-row-count { font-size: 12px; color: #94a3b8; font-weight: 600; background: #f1f5f9; padding: 3px 10px; border-radius: 20px; }

/* Loading / Empty */
.cu-loading { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 60px 20px; color: #64748b; font-size: 14px; }
.cu-spinner { width: 24px; height: 24px; border: 3px solid #e2e8f0; border-top-color: #059669; border-radius: 50%; animation: cu-spin 0.8s linear infinite; }
@keyframes cu-spin { to { transform: rotate(360deg); } }
.cu-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; color: #94a3b8; text-align: center; }
.cu-empty i { font-size: 52px; color: #059669; margin-bottom: 12px; }
.cu-empty h5 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0 0 6px; }
.cu-empty p { font-size: 13px; margin: 0; }

/* Table */
.cu-table-wrap { overflow-x: auto; }
.cu-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.cu-table th { background: #f8fafc; padding: 11px 14px; text-align: left; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
.cu-table td { padding: 12px 14px; border-bottom: 1px solid #f1f5f9; color: #334155; vertical-align: middle; }
.cu-table tr:last-child td { border-bottom: none; }
.cu-table tr:hover td { background: #f8fafc; }
.cu-td-num { color: #94a3b8; font-size: 12px; font-weight: 600; width: 40px; }

.cu-employee-cell { display: flex; align-items: center; gap: 10px; }
.cu-avatar {
  width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #059669, #0d9488);
  color: #fff; font-size: 11px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.cu-mono { font-family: monospace; font-size: 12px; color: #475569; }

.cu-days-badge { padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 700; }
.cu-days-urgent { background: #fef2f2; color: #ef4444; }
.cu-days-ok { background: #f0fdf4; color: #059669; }
.cu-status-pill { background: #eff6ff; color: #2563eb; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; }

.cu-send-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 12px; border-radius: 7px; border: 1px solid #bfdbfe;
  background: #eff6ff; color: #2563eb; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.15s; white-space: nowrap;
}
.cu-send-btn:hover:not(:disabled) { background: #2563eb; color: #fff; border-color: #2563eb; }
.cu-send-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.cu-send-loading { background: #f8fafc; color: #94a3b8; border-color: #e2e8f0; }

@media (max-width: 900px) {
  .cu-summary-row { grid-template-columns: 1fr 1fr; }
  .cu-card-chart { grid-column: span 2; }
  .cu-tabs { flex-wrap: wrap; }
}
@media (max-width: 600px) {
  .cu-summary-row { grid-template-columns: 1fr; }
  .cu-card-chart { grid-column: span 1; }
  .cu-toolbar { flex-direction: column; align-items: stretch; }
  .cu-toolbar-right { justify-content: flex-end; }
}
</style>
